import { Steps, Typography, Tooltip } from "antd";
import type { StepProps, StepsProps } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

import { Value } from "@components/Text";
import { DATETIME_FORMAT } from "@components/Date";

import DocusignLink from "./DocusignLink";
import InvestmentSignedDropdown from "./InvestmentSignedDropdown";
import type { Investment, ReadInvestment } from "../../types";

const LABEL_SIGNED = "Signed";
const LABEL_FUNDED = "Funded";
const LABEL_SIGNING = "Signing";
const LABEL_FUNDING = "Funding";
const LABEL_CREATED = "Submitted";
const LABEL_FUNDS_SENT = "Funds Sent";
const LABEL_OVERFUNDED = "Overfunded";
const LABEL_PARTIALLY_FUNDED = "Partially Funded";
const LABEL_FUNDS_SENT_TOOLTIP = "Investor claimed that funds have been sent.";

const FUNDED_STEP_STYLE = { minWidth: 200 };

type Props = {
  investment: Investment;
  readInvestment: ReadInvestment;
  isInvestmentSignedTemplateEnabled: () => boolean;
};

const InvestmentSteps = ({
  investment,
  readInvestment,
  isInvestmentSignedTemplateEnabled,
}: Props) => {
  const {
    createdAt,
    fundedAt,
    isSigned,
    isFunded,
    hasSentFunds,
    isOverfunded,
    receivedAmount,
    isPartiallyFunded,
    subscriptionAdminUrl,
  } = investment;

  const format = DATETIME_FORMAT;

  const signedStatus: StepProps["status"] = isSigned
    ? "finish"
    : "process";

  const getFundedStatus = (): StepProps["status"] => {
    if (!isSigned) {
      return "wait";
    }

    return isFunded ? "finish" : "process";
  };

  const getFundedTitle = () => {
    if (isFunded) {
      return LABEL_FUNDED;
    }

    if (isPartiallyFunded) {
      return LABEL_PARTIALLY_FUNDED;
    }

    if (isOverfunded) {
      return LABEL_OVERFUNDED;
    }

    return LABEL_FUNDING;
  };

  const getFundedDescription = () => {
    if (isFunded) {
      return <Value type="secondary" value={fundedAt} format={format} />;
    }

    if (isOverfunded) {
      return <Value type="secondary" value={receivedAmount} />;
    }

    if (isPartiallyFunded) {
      return (
        <Value type="secondary" value={receivedAmount} />
      );
    }

    return hasSentFunds
      ? (
        <Typography.Text type="secondary">
          {LABEL_FUNDS_SENT}{" "}
          <Tooltip title={LABEL_FUNDS_SENT_TOOLTIP}>
            <QuestionCircleOutlined />
          </Tooltip>
        </Typography.Text>
      )
      : "";
  };

  const items: StepsProps["items"] = [
    {
      title: LABEL_CREATED,
      status: "finish",
      description: <Value type="secondary" value={createdAt} format={format} />,
    },
    {
      title: isSigned ? LABEL_SIGNED : LABEL_SIGNING,
      subTitle: isSigned && subscriptionAdminUrl && <DocusignLink href={subscriptionAdminUrl} />,
      status: signedStatus,
      description: (
        <InvestmentSignedDropdown
          investment={investment}
          readInvestment={readInvestment}
          isInvestmentSignedTemplateEnabled={isInvestmentSignedTemplateEnabled}
        />
      ),
    },
    {
      title: getFundedTitle(),
      status: getFundedStatus(),
      description: getFundedDescription(),
      style: FUNDED_STEP_STYLE,
    },
  ];

  const stepsProps: StepsProps = {
    className: "steps-inverted-colors",
    items,
  };

  return (
    <Steps {...stepsProps} />
  );
};

export default InvestmentSteps;
